import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CircleCheck, Wrench, PackageCheck, TruckIcon, Hash, ShieldCheck, ChevronsRight, MapPin, HelpCircle, Users, Factory, Store, Building2 } from "lucide-react";

export function InfoSection() {
  const items = [
    { icon: Hash, title: "Vehicle compatibility", body: "Year, make, model, and engine information on every listing." },
    { icon: PackageCheck, title: "Part-number matching", body: "OEM, manufacturer, and interchange numbers where available." },
    { icon: CircleCheck, title: "Product condition", body: "OEM, Remanufactured, and Used items are clearly labeled." },
    { icon: Wrench, title: "Testing information", body: "Test reports and bench-testing notes provided when confirmed." },
    { icon: ShieldCheck, title: "Warranty details", body: "Listed warranty terms appear on each product page." },
    { icon: TruckIcon, title: "Nationwide shipping", body: "United States shipping via USPS and UPS with tracking." },
  ];
  return (
    <section data-testid="home-info" className="bg-slate-50 py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#0055FF]">Product information</div>
          <h2 className="mt-2 font-heading text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">Clear product information before you order</h2>
          <p className="text-slate-600 mt-3">Every listing is structured to show the product condition, manufacturer, quantity, part numbers, vehicle applications, testing status, and warranty information available for that item.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it, i) => (
            <div key={it.title} className="rounded-xl border border-slate-200 bg-white p-6 ici-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
              <div className="w-10 h-10 rounded-md bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center">
                <it.icon className="w-5 h-5" />
              </div>
              <div className="mt-4 font-heading font-semibold text-slate-900">{it.title}</div>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  const steps = [
    { t: "Search by vehicle or part number", d: "Use our vehicle finder or the OEM/interchange search." },
    { t: "Review compatibility and product details", d: "Check the exact fitment, condition, and part numbers." },
    { t: "Purchase securely or continue through eBay", d: "Check out on-site with Stripe or open the eBay listing." },
    { t: "Receive tracking and delivery updates", d: "Nationwide shipping with tracking information provided." },
  ];
  return (
    <section data-testid="home-how" className="bg-white py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="max-w-2xl mb-10">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#0055FF]">How it works</div>
          <h2 className="mt-2 font-heading text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">Four steps from search to delivery</h2>
        </div>
        <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <li key={i} className="relative rounded-xl border border-slate-200 bg-white p-6 ici-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="font-mono text-4xl font-bold text-slate-100 leading-none">{String(i + 1).padStart(2, "0")}</div>
              <div className="mt-4 font-heading font-semibold text-slate-900">{s.t}</div>
              <p className="text-sm text-slate-500 mt-1">{s.d}</p>
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-10 -right-2 text-slate-200">
                  <ChevronsRight />
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function Commercial() {
  const buyers = [
    { icon: Wrench, t: "Independent mechanics" },
    { icon: Store, t: "Repair shops" },
    { icon: Building2, t: "Dealerships" },
    { icon: TruckIcon, t: "Fleet operators" },
    { icon: Factory, t: "Resellers" },
    { icon: Users, t: "Wholesale buyers" },
  ];
  return (
    <section data-testid="home-commercial" className="bg-[#151A26] text-white py-16 lg:py-24 relative overflow-hidden">
      <div className="absolute inset-0 ici-grid-bg-dark opacity-40" />
      <div className="max-w-7xl mx-auto px-4 lg:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#94A3B8]">Wholesale</div>
            <h2 className="mt-2 font-heading text-3xl lg:text-4xl font-bold tracking-tight">Automotive supply for professionals</h2>
            <p className="mt-3 text-slate-400 max-w-md">Volume pricing, recurring inventory, product sourcing, and multi-unit order support for qualified commercial buyers.</p>
            <Button asChild data-testid="commercial-cta" className="mt-6 bg-[#0055FF] hover:bg-[#0044CC]">
              <Link to="/wholesale">Request Commercial Pricing</Link>
            </Button>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {buyers.map((b, i) => (
              <div key={b.t} className="rounded-lg border border-white/10 bg-white/5 backdrop-blur p-5 ici-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                <b.icon className="w-5 h-5 text-[#0055FF]" />
                <div className="mt-3 font-heading font-semibold text-white text-sm">{b.t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function MarketplaceReputation() {
  return (
    <section data-testid="home-marketplace" className="bg-white py-16 lg:py-24">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 text-center">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#0055FF]">Marketplace reputation</div>
        <h2 className="mt-2 font-heading text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">Verified feedback on our official eBay store</h2>
        <p className="mt-4 text-slate-500 max-w-2xl mx-auto">Reviews shown on this site reflect our real eBay marketplace feedback. We do not publish unverified testimonials.</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-6">
          <div className="text-center">
            <div className="font-display text-5xl font-extrabold text-slate-900">100%</div>
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-1">Positive feedback</div>
          </div>
          <div className="w-px h-14 bg-slate-200" />
          <div className="text-center">
            <div className="font-display text-5xl font-extrabold text-slate-900">USA</div>
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-1">Located in</div>
          </div>
        </div>
        <a
          data-testid="marketplace-ebay-link"
          href="https://www.ebay.com/usr/intercorp_industries"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-700"
        >
          View verified feedback on eBay
        </a>
      </div>
    </section>
  );
}

export function FitmentCTA() {
  return (
    <section data-testid="home-fitment-cta" className="bg-slate-50 py-16 lg:py-24">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#0055FF]">Fitment assistance</div>
          <h2 className="mt-2 font-heading text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">Not certain about fitment?</h2>
          <p className="mt-3 text-slate-600 max-w-2xl">Send us your vehicle information, VIN, and existing part number. Our team will review the available product data and help you identify the appropriate listing.</p>
        </div>
        <Button asChild data-testid="home-fitment-button" className="h-12 px-6 bg-[#0055FF] hover:bg-[#0044CC]">
          <Link to="/fitment-assistance">Request Fitment Assistance</Link>
        </Button>
      </div>
    </section>
  );
}

export function KnowledgeCards() {
  const cards = [
    "How to Find Your Fuel Injector Part Number",
    "OEM vs. Aftermarket Fuel Injectors",
    "Single Injector vs. Complete Injector Set",
    "What Remanufactured Fuel Injectors Mean",
    "Direct Injection vs. Port Injection",
    "How to Confirm Vehicle Compatibility",
    "Common Symptoms of Injector Problems",
    "Why Engine Size Matters",
  ];
  const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <section data-testid="home-knowledge" className="bg-white py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#0055FF]">Knowledge Center</div>
            <h2 className="mt-2 font-heading text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">Learn about injectors, fitment, and compatibility</h2>
          </div>
          <Link to="/knowledge" className="text-sm text-slate-600 hover:text-[#0055FF] inline-flex items-center gap-1">
            View all articles <ChevronsRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((c, i) => (
            <Link key={c} to={`/knowledge/${slug(c)}`} className="ici-card-lift group rounded-xl border border-slate-200 bg-white p-5 ici-fade-up" style={{ animationDelay: `${(i % 4) * 60}ms` }}>
              <HelpCircle className="w-6 h-6 text-[#0055FF]" />
              <div className="mt-4 font-heading font-semibold text-slate-900 group-hover:text-[#0055FF] transition-colors">{c}</div>
              <div className="mt-3 text-xs font-mono text-slate-400 uppercase tracking-widest">Read article →</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FinalCTA() {
  return (
    <section data-testid="home-finalcta" className="bg-[#0B0F19] text-white py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute inset-0 ici-grid-bg-dark opacity-40" />
      <div className="absolute inset-0 ici-radial pointer-events-none" />
      <div className="max-w-4xl mx-auto px-4 lg:px-8 text-center relative">
        <h2 className="font-display text-4xl lg:text-6xl font-extrabold tracking-tight">Find the correct component with confidence.</h2>
        <p className="mt-4 text-slate-400 max-w-2xl mx-auto">Vehicle-verified fitment. Real product data. Direct communication with our team when you need help.</p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Button asChild data-testid="finalcta-shop" className="bg-[#0055FF] hover:bg-[#0044CC] h-12 px-6">
            <Link to="/shop">Browse inventory</Link>
          </Button>
          <Button asChild data-testid="finalcta-partnumber" variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10 h-12 px-6">
            <Link to="/search/part">Search by part number</Link>
          </Button>
          <Button asChild data-testid="finalcta-contact" variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10 h-12 px-6">
            <Link to="/contact">Contact InterCorp Industries</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
